import { useEffect, useState } from "react";
import api from "../lib/api";
import { useAuth } from "../lib/auth";
import { Sparkles, Clock, Gift, Coins, Info, Send, Loader2 } from "lucide-react";
import { motion } from "framer-motion";

export default function Predict() {
  const { user, refresh } = useAuth();
  const [current, setCurrent] = useState(null);
  const [history, setHistory] = useState([]);
  const [instr, setInstr] = useState("");
  const [amount, setAmount] = useState(50);
  const [prediction, setPrediction] = useState("");
  const [busy, setBusy] = useState(false);
  const [ok, setOk] = useState("");
  const [err, setErr] = useState("");

  const load = () => {
    api.get("/predict/current").then(r => {
      const rec = r.data?.id ? r.data : null;
      setCurrent(rec);
      if (rec?.min_goldly && rec.min_goldly > 0) setAmount(a => Math.max(a || 0, rec.min_goldly));
    });
    api.get("/predict/history").then(r => setHistory(r.data));
    api.get("/predict/instructions").then(r => setInstr(r.data?.content || "")).catch(() => {});
  };
  useEffect(() => { load(); const t = setInterval(load, 30000); return () => clearInterval(t); }, []);

  const minG = current?.min_goldly || 0;

  const submit = async (e) => {
    e?.preventDefault?.();
    setErr(""); setOk("");
    if (!current?.id) { setErr("No active reveal"); return; }
    const amt = parseInt(amount) || 0;
    if (amt <= 0) { setErr("Enter a positive Goldly amount"); return; }
    if (minG && amt < minG) { setErr(`Minimum stake for this reveal is ${minG} Goldly`); return; }
    setBusy(true);
    try {
      const { data } = await api.post("/predict/submit", { reveal_id: current.id, goldly_amount: amt, prediction });
      setOk(`✓ Prediction submitted! ${amt} Goldly staked. New balance: ${data.new_balance_goldly}`);
      setPrediction("");
      refresh?.();
    } catch (e) { setErr(e?.response?.data?.detail || "Submission failed"); }
    finally { setBusy(false); }
  };

  const bal = user?.wallet_goldly ?? 0;

  return (
    <div className="px-4 py-6 pb-24" data-testid="predict-page">
      <div className="flex items-center gap-2 mb-1">
        <Sparkles className="w-7 h-7 text-[#FF2A5F]"/>
        <h1 className="font-outfit font-extrabold text-3xl tracking-tighter">Predict & Enjoy</h1>
      </div>
      <p className="text-white/60 text-sm mb-5">Hourly lucky-draw revealed by Hifireels Admin</p>

      {/* Admin Instructions box */}
      {instr && (
        <div data-testid="predict-instructions-box" className="mb-4 rounded-2xl bg-[#FF7A00]/10 border border-[#FF7A00]/40 p-4 flex gap-3">
          <Info className="w-5 h-5 text-[#FF7A00] flex-shrink-0 mt-0.5"/>
          <div className="text-sm text-white/85 whitespace-pre-line leading-relaxed">{instr}</div>
        </div>
      )}

      {current ? (
        <motion.div initial={{scale:0.95, opacity:0}} animate={{scale:1, opacity:1}}
          className="rounded-3xl overflow-hidden bg-gradient-to-br from-[#FF2A5F] via-[#FF6B35] to-[#FF7A00] p-[2px] mb-4 neon-pink">
          <div className="rounded-3xl bg-[#0B0F19] p-5 text-center">
            <div className="text-xs uppercase tracking-wider text-[#FF7A00] font-bold mb-1">Latest Reveal</div>
            {current.video_url ? (
              <video src={current.video_url} controls autoPlay muted loop playsInline className="w-full rounded-2xl mb-3 aspect-video object-cover bg-black" data-testid="predict-video"/>
            ) : current.image_url ? (
              <img src={current.image_url} alt={current.title} className="w-full rounded-2xl mb-3 object-cover" data-testid="predict-image"/>
            ) : (
              <div className="text-7xl mb-3">{current.reward_emoji}</div>
            )}
            <h2 className="font-outfit font-extrabold text-2xl mb-2">{current.title}</h2>
            <div className="inline-block px-4 py-2 rounded-full bg-[#FF7A00]/15 border border-[#FF7A00]/40 text-[#FF7A00] font-semibold text-sm" data-testid="predict-reward">
              {current.reward_text}
            </div>
            <div className="mt-3 text-[10px] text-white/50 flex items-center justify-center gap-1">
              <Clock className="w-3 h-3"/>{new Date(current.revealed_at).toLocaleString()}
            </div>
          </div>
        </motion.div>
      ) : (
        <div className="rounded-3xl bg-white/5 border border-white/10 p-8 text-center mb-6">
          <Gift className="w-12 h-12 text-[#FF7A00] mx-auto mb-2"/>
          <p className="text-white/70">Waiting for next admin reveal...</p>
        </div>
      )}

      {/* Manual submission form */}
      {current && (
        <form onSubmit={submit} data-testid="predict-submit-form" className="rounded-3xl bg-gradient-to-br from-[#A855F7]/15 to-[#00E5FF]/10 border border-[#A855F7]/40 p-4 mb-6 space-y-3">
          <div className="flex items-center gap-2">
            <Send className="w-4 h-4 text-[#A855F7]"/>
            <h3 className="font-outfit font-bold text-sm">Stake Goldly on your prediction</h3>
          </div>
          <div className="text-[11px] text-white/60 flex items-center justify-between">
            <span>Your balance: <b className="text-[#FF7A00]">🪙 {bal}</b></span>
            {minG > 0 && <span>Min stake: <b className="text-[#FF7A00]">{minG} Goldly</b></span>}
          </div>
          <div className="flex items-center gap-2">
            <div className="flex-1 relative">
              <Coins className="w-4 h-4 text-[#FF7A00] absolute left-3 top-1/2 -translate-y-1/2"/>
              <input
                data-testid="predict-amount"
                type="number"
                min={minG || 1}
                inputMode="numeric"
                value={amount}
                onChange={e => setAmount(e.target.value.replace(/[^0-9]/g, ""))}
                placeholder={`Enter Goldly (min ${minG || 1})`}
                className="w-full pl-10 pr-3 py-3 rounded-2xl bg-white/5 border border-white/15 outline-none focus:border-[#FF7A00] font-bold"
              />
            </div>
          </div>
          {/* Quick tier chips */}
          <div className="flex gap-1.5 flex-wrap" data-testid="predict-tiers">
            {[10, 50, 100, 500, 1000].filter(v => !minG || v >= minG).map(v => (
              <button key={v} type="button" onClick={() => setAmount(v)} data-testid={`predict-tier-${v}`}
                className={`px-3 py-1.5 rounded-full text-[11px] font-bold border active:scale-95 ${parseInt(amount) === v ? "bg-[#FF7A00] text-black border-[#FF7A00]" : "bg-white/5 border-white/15 text-white/75"}`}>
                🪙 {v}
              </button>
            ))}
          </div>
          <textarea
            data-testid="predict-message"
            rows={2}
            value={prediction}
            onChange={e => setPrediction(e.target.value)}
            placeholder="Your prediction / message to admin (optional)…"
            className="w-full px-3 py-2 rounded-2xl bg-white/5 border border-white/10 outline-none text-sm placeholder:text-white/40"
            maxLength={500}
          />
          {err && <div data-testid="predict-err" className="text-[#FF2A5F] text-xs">{err}</div>}
          {ok && <div data-testid="predict-ok" className="text-emerald-400 text-xs">{ok}</div>}
          <button type="submit" disabled={busy} data-testid="predict-submit-btn"
            className="w-full py-3 rounded-full bg-gradient-to-r from-[#FF2A5F] to-[#A855F7] text-white font-outfit font-extrabold flex items-center justify-center gap-2 active:scale-95 disabled:opacity-50">
            {busy ? <Loader2 className="w-4 h-4 animate-spin"/> : <Send className="w-4 h-4"/>}
            {busy ? "Submitting…" : `Submit · Stake ${parseInt(amount) || 0} Goldly`}
          </button>
          <p className="text-[10px] text-white/45 text-center">
            Goldly will be deducted from your wallet and credited to <b className="text-[#A855F7]">@predict_and_enjoy</b>.
            Your prediction is DM&apos;d to admin for tracking.
          </p>
        </form>
      )}

      <h2 className="font-outfit font-bold text-xl mb-3">Past Reveals</h2>
      <div className="space-y-2" data-testid="predict-history">
        {history.slice(1).map(h => (
          <div key={h.id} className="flex items-center gap-3 p-3 rounded-2xl bg-white/5 border border-white/10">
            <div className="text-3xl">{h.reward_emoji}</div>
            <div className="flex-1">
              <div className="font-semibold text-sm">{h.title}</div>
              <div className="text-xs text-white/60">{h.reward_text}</div>
              <div className="text-[10px] text-white/40 mt-0.5">{new Date(h.revealed_at).toLocaleString()}</div>
            </div>
          </div>
        ))}
        {history.length <= 1 && <p className="text-center text-white/40 text-sm py-4">No history yet</p>}
      </div>
    </div>
  );
}
